import { useEffect, useState } from 'react'

import { default as Statuses } from '@cinch/oxd-utils/request-status/src'
import { WbacService } from '@cinch/oxd-utils/requests/src'
import * as Storage from '@cinch/oxd-utils/storage/src'

import { Journey, MultipleChoiceAnswerOptionsItem, PageQuestionsItem, QuestionControlType } from '../../types'

export interface NextParams {
  requestedParameterName: string
  filterParameterDictionary: FilterParameterDictionary
}

interface FilterParameterDictionary {
  BookId: number
  Manufacturer: string
  Model: string
  FuelType: string
  RegYear: string
  Colour: string
  MetallicPaint: string
  Derivative: string
}

interface NextResponse {
  parameterName: string
  valueDisplayDictionary: {
    [key: string]: string
  }
}

const markDynamicQuestions = (journey: Journey): Journey => {
  journey.journeyPages.forEach((page) => {
    page.pageQuestions.forEach((question) => {
      const [content] = question.questionsDisplayContent
      content.hasDynamicChoiceAnswerOptions = 0 === content.multipleChoiceAnswerOptions.length
    })
  })

  return journey
}

const fetchJourney = async () => {
  const { token: storageToken } = Storage.get(Storage.StorageKeys.wbac)
  const result = await WbacService.get('/manuallookup/manuallookup', {
    headers: { Authorization: `JWT ${storageToken}` },
  })

  return result.data
}

const getNext = async (options: any) => {
  const { token: storageToken } = Storage.get(Storage.StorageKeys.wbac)
  const result = await WbacService.post<NextResponse>('/manuallookup/next', options, {
    headers: { Authorization: `JWT ${storageToken}` },
  })

  return result.data
}

const useJourney = () => {
  const [journey, setJourney] = useState<Journey | undefined>(undefined)
  const [status, setStatus] = useState<string>(Statuses.INITIAL)
  const [nextStatus, setNextStatus] = useState<string>(Statuses.INITIAL)

  useEffect(() => {
    setStatus(Statuses.PENDING)
    fetchJourney().then((response) => {
      setJourney(markDynamicQuestions(response.journey))
      setStatus(Statuses.SUCCESS)
    })
  }, [])

  /**
   * this method fetches the posible options for a question and updates the journey with those options.
   */
  const getQuestionOptions = (options: NextParams) => {
    const newJourney = { ...journey } as Journey
    const [firstPage] = newJourney.journeyPages
    const nextQuestion = firstPage.pageQuestions.find(({ questionParameterName }) => {
      return questionParameterName === options.requestedParameterName
    })

    setNextStatus(Statuses.PENDING)
    getNext(options).then((response: NextResponse) => {
      if (!response) {
        return
      }

      const multipleChoiceAnswerOptions = Object.keys(response.valueDisplayDictionary).map(
        (optionValue): MultipleChoiceAnswerOptionsItem => ({
          optionDisplayText: response.valueDisplayDictionary[optionValue],
          optionValue,
        }),
      )

      if (nextQuestion) {
        nextQuestion.questionsDisplayContent[0].multipleChoiceAnswerOptions = multipleChoiceAnswerOptions
      }
      setJourney(newJourney)
      setNextStatus(Statuses.SUCCESS)
    })
  }

  const resetFieldOptions = (requestedParameterName: string) => {
    const newJourney = { ...journey } as Journey
    const [firstPage] = newJourney.journeyPages
    const nextQuestion = firstPage.pageQuestions.find(({ questionParameterName }) => {
      return questionParameterName === requestedParameterName
    })

    if (nextQuestion) {
      const canReset = nextQuestion.questionControlType === QuestionControlType.ListDropdown

      if (!canReset) {
        return
      }

      // set deep question options to empty array
      nextQuestion.questionsDisplayContent[0].multipleChoiceAnswerOptions = []
    }

    setJourney(newJourney)
  }

  const getParentQuestion = (requestedParameterName: string): PageQuestionsItem | undefined => {
    const newJourney = { ...journey } as Journey
    const [firstPage] = newJourney.journeyPages

    return firstPage.pageQuestions.find(({ questionParameterName }) => {
      return questionParameterName === requestedParameterName
    })
  }

  return {
    journey,
    status,
    getQuestionOptions,
    nextStatus,
    resetFieldOptions,
    getParentQuestion,
  }
}

export default useJourney
